import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layers } from 'lucide-react';

import {
  useGetAvatarSeriesQuery,
  type AvatarItem,
} from '@/services/avatarApi';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

interface AvatarCarouselProps {
  avatar: AvatarItem;
  className?: string;
}

/**
 * Multi-version avatar card. Renders a horizontal scroll-snap pager so the
 * user can swipe sideways through every version of the series in place
 * (page-flip / "sfoglia" feel). Lazy-fetches the series only when the card
 * enters the viewport. Fires a `selection` haptic on each new active slide
 * and a small indicator pulse on the page indicator.
 */
export function AvatarCarousel({ avatar, className }: AvatarCarouselProps) {
  const aspectClass = avatar.aspectRatio === '3:4' ? 'aspect-[3/4]' : 'aspect-square';

  const visibilityRef = useRef<HTMLDivElement | null>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    const node = visibilityRef.current;
    if (!node || hasBeenVisible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setHasBeenVisible(true);
      },
      { rootMargin: '300px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasBeenVisible]);

  const { data: series } = useGetAvatarSeriesQuery(avatar._id, {
    skip: !hasBeenVisible,
  });

  const versions = series && series.length > 0 ? series : [avatar];

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const haptics = useHaptics();
  const hapticsRef = useRef(haptics);
  hapticsRef.current = haptics;

  const [activeIdx, setActiveIdx] = useState(0);
  const activeIdxRef = useRef(0);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const settle = () => {
      const idx = Math.round(node.scrollLeft / Math.max(1, node.clientWidth));
      if (idx !== activeIdxRef.current) {
        activeIdxRef.current = idx;
        setActiveIdx(idx);
        hapticsRef.current.selection();
      }
    };
    const onScroll = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(settle, 120);
    };

    node.addEventListener('scroll', onScroll, { passive: true });
    node.addEventListener('scrollend', settle);
    return () => {
      if (timer) clearTimeout(timer);
      node.removeEventListener('scroll', onScroll);
      node.removeEventListener('scrollend', settle);
    };
  }, [versions.length]);

  const total = versions.length;
  const showIndicator = total > 1;

  return (
    <div
      ref={visibilityRef}
      className={cn(
        'relative w-full overflow-hidden rounded-2xl bg-bg-tertiary',
        aspectClass,
        className,
      )}
    >
      <div
        ref={scrollRef}
        className="no-scrollbar flex h-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {versions.map((v) => (
          <Link
            key={v._id}
            to={`/avatars/${v._id}`}
            className="relative block h-full w-full shrink-0 snap-start snap-always"
            style={{ scrollSnapStop: 'always' }}
          >
            {v.imageUrl || v.thumbnailUrl ? (
              <img
                src={v.imageUrl || v.thumbnailUrl}
                alt={v.title}
                className="h-full w-full object-cover"
                loading="lazy"
                draggable={false}
              />
            ) : (
              <div className="h-full w-full bg-bg-tertiary" />
            )}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-4">
              <h3 className="truncate text-base font-semibold text-white">{v.title}</h3>
            </div>
          </Link>
        ))}
      </div>

      {showIndicator && (
        <>
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1',
              'rounded-full border border-brand/60 bg-black/70 px-2 py-1 text-[11px] font-bold text-white backdrop-blur',
            )}
          >
            <Layers className="h-3 w-3 text-brand" aria-hidden />
            <span>
              {activeIdx + 1}/{total}
            </span>
          </div>

          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center gap-1.5"
          >
            {versions.map((_, idx) => (
              <span
                key={idx}
                className={cn(
                  'block h-1.5 rounded-full transition-all duration-200',
                  idx === activeIdx ? 'w-4 bg-brand' : 'w-1.5 bg-white/50',
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
